// src/services/activeSensorAlertService.js
import { authenticatedFetch } from './api';

const API_ACTIVE_ALERTS_URL = '/active-alerts'; // Relative to API base

/**
 * Fetches active sensor alerts with pagination and filters.
 * Enhanced with Auto-Pagination: Automatically handles fetching >100 items by chaining requests,
 * and sanitizes invalid filter values (like status='all') to prevent backend 400 errors.
 * * @param {object} [params={}] e.g., { page, limit, sortBy, sortOrder, sensor_id, asset_id, status, severity, search }
 * @returns {Promise<any>}
 */
export const fetchActiveAlerts = async (params = {}) => {
    // 1. Sanitize Parameters to prevent Backend Validation Errors (400 Bad Request)
    const safeParams = { ...params };
    if (safeParams.status === 'all') {
        delete safeParams.status; // Backend expects no status param if we want 'all'
    }

    // 2. Auto-Pagination Logic for limits > 100
    if (safeParams.limit && safeParams.limit > 100) {
        console.log(`[ActiveAlertService] Requested limit ${safeParams.limit} exceeds backend max (100). Switching to auto-pagination.`);
        
        let allData = [];
        let page = 1;
        let hasMore = true;
        const targetTotal = safeParams.limit;
        
        // Create filters for the individual page requests (max 100 per call)
        const pageParams = { ...safeParams, limit: 100 };
        
        try {
            while (hasMore && allData.length < targetTotal) {
                pageParams.page = page;
                
                // Recursive call: effectively calls the "Standard Logic" block below
                const result = await fetchActiveAlerts(pageParams);
                
                if (result.data && Array.isArray(result.data) && result.data.length > 0) {
                    allData = [...allData, ...result.data];
                    
                    // Check pagination metadata to see if we reached the end
                    if (result.pagination && page >= result.pagination.totalPages) {
                        hasMore = false;
                    } else {
                        page++;
                    }
                } else {
                    hasMore = false;
                }
            }
            
            console.log(`[ActiveAlertService] Auto-pagination complete. Fetched ${allData.length} total records.`);
            
            // Return a synthesized response structure matching the API
            return {
                data: allData.slice(0, targetTotal),
                pagination: {
                    totalItems: allData.length,
                    totalPages: 1,
                    currentPage: 1,
                    limit: targetTotal
                }
            };
        } catch (error) {
            console.error("[ActiveAlertService] Auto-pagination failed:", error);
            throw error;
        }
    }

    // 3. Standard Logic (For limits <= 100)
    const queryParams = new URLSearchParams();
    for (const key in safeParams) {
        if (safeParams[key] !== null && safeParams[key] !== undefined && safeParams[key] !== '') {
            queryParams.append(key, safeParams[key]);
        }
    }
    
    const endpoint = `${API_ACTIVE_ALERTS_URL}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    console.log(`[ActiveAlertService] Fetching active alerts from: ${endpoint}`);
    
    try {
        const responseData = await authenticatedFetch(endpoint);
        return responseData || { data: [], pagination: {} }; // Ensure data and pagination always exist
    } catch (error) {
        console.error("[ActiveAlertService] Error fetching active alerts:", error);
        throw error;
    }
};

/**
 * Fetches a single active sensor alert by ID.
 * @param {number} alertId
 * @returns {Promise<any>}
 */
export const fetchActiveAlertById = async (alertId) => {
    if (!alertId) throw new Error("Alert ID is required.");
    const endpoint = `${API_ACTIVE_ALERTS_URL}/${alertId}`;
    console.log(`[ActiveAlertService] Fetching active alert ID: ${alertId}`);
    try {
        return await authenticatedFetch(endpoint);
    } catch (error) {
        console.error(`[ActiveAlertService] Error fetching active alert ${alertId}:`, error);
        throw error;
    }
};

/**
 * Updates an active sensor alert (e.g., status, notes).
 * @param {number} alertId
 * @param {object} updateData
 * @returns {Promise<any>}
 */
export const updateActiveAlert = async (alertId, updateData) => {
    if (!alertId) throw new Error("Alert ID is required for update.");
    const endpoint = `${API_ACTIVE_ALERTS_URL}/${alertId}`;
    console.log(`[ActiveAlertService] Updating active alert ID ${alertId}:`, updateData);
    const options = {
        method: 'PUT',
        body: JSON.stringify(updateData),
    };
    try {
        return await authenticatedFetch(endpoint, options);
    } catch (error) {
        console.error(`[ActiveAlertService] Error updating active alert ${alertId}:`, error);
        throw error;
    }
};

/**
 * Manually creates an active sensor alert.
 * @param {object} alertData
 * @returns {Promise<any>}
 */
export const createActiveAlert = async (alertData) => {
    const endpoint = API_ACTIVE_ALERTS_URL;
    console.log("[ActiveAlertService] Creating new active alert:", alertData);
    const options = {
        method: 'POST',
        body: JSON.stringify(alertData),
    };
    try {
        return await authenticatedFetch(endpoint, options);
    } catch (error) {
        console.error("[ActiveAlertService] Error creating active alert:", error);
        throw error;
    }
};

/**
 * Deletes an active sensor alert.
 * @param {number} alertId
 * @returns {Promise<any>}
 */
export const deleteActiveAlert = async (alertId) => {
    if (!alertId) throw new Error("Alert ID is required for deletion.");
    const endpoint = `${API_ACTIVE_ALERTS_URL}/${alertId}`;
    console.log(`[ActiveAlertService] Deleting active alert ID: ${alertId}`);
    const options = {
        method: 'DELETE',
    };
    try {
        return await authenticatedFetch(endpoint, options);
    } catch (error) {
        console.error(`[ActiveAlertService] Error deleting active alert ${alertId}:`, error);
        throw error;
    }
};
// ═════════════════════════════════════════════════════════════════════════════
// --- UPDATE (Alert System AI) — NEW calls
//   fetchAlertStats()            counts by status / severity + rule counts (one request; replaces
//                                fetchActiveAlerts({ limit: 10000 }), which paged 100 requests)
//   fetchAlertIntelligence()     FlowSense AI snapshot: health, KPIs, trend, triage, incidents,
//                                noisy / flapping rules, silent sensors, insights
//   bulkUpdateActiveAlerts()     acknowledge / resolve / note many alerts in one request
//   fetchAlertReadings()         sensor readings + thresholds + learned baseline for one alert
// ═════════════════════════════════════════════════════════════════════════════
const unwrap = (res) => (res && res.success !== undefined && res.data !== undefined ? res.data : res);

export const fetchAlertStats = async () => unwrap(await authenticatedFetch(`${API_ACTIVE_ALERTS_URL}/stats`));

export const fetchAlertIntelligence = async ({ refresh = false } = {}) =>
    unwrap(await authenticatedFetch(`${API_ACTIVE_ALERTS_URL}/intelligence${refresh ? '?refresh=1' : ''}`));

/**
 * @param {number[]} ids
 * @param {{ status?: string, notes?: string }} change
 * @returns {Promise<{ updated: number, failed: number, results: Array }>}
 */
export const bulkUpdateActiveAlerts = async (ids, change = {}) =>
    authenticatedFetch(`${API_ACTIVE_ALERTS_URL}/bulk`, {
        method: 'POST',
        body: JSON.stringify({ ids, ...change }),
    });

export const fetchAlertReadings = async (alertId, hours = 24) => {
    if (!alertId) throw new Error('Alert ID is required.');
    return unwrap(await authenticatedFetch(`${API_ACTIVE_ALERTS_URL}/${alertId}/readings?hours=${encodeURIComponent(hours)}`));
};
// --- END UPDATE
